import React, { useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material";
import { useRebrandBanner } from "../../hooks/useRebrandBanner";
import {
  registerModalLayer,
  unregisterModalLayer,
} from "../../utils/modalLayer";

// Global modal stack for nested modals management
let modalStack = [];
let popstateListenerAdded = false;

const cleanModalStack = () => {
  modalStack = modalStack.filter((modal) => modal.isOpen);
};

// Global handler for browser back button
const onGlobalPopState = () => {
  const topModal = modalStack[modalStack.length - 1];
  if (topModal && topModal.isOpen) {
    modalStack = modalStack.filter((modal) => modal.id !== topModal.id);
    topModal.hasPushedHistory = false;
    topModal.onClose && topModal.onClose();
  }
};

const addGlobalPopstateListener = () => {
  if (!popstateListenerAdded) {
    window.addEventListener("popstate", onGlobalPopState);
    popstateListenerAdded = true;
  }
};

const generateModalId = () =>
  `modal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const ModalBasicLayout = ({
  children,
  modalOpen,
  onCloseModal,
  title,
  ariaLabel = "Modal génerico",
  maxWidth = "sm",
  fullWidth = false,
  scroll = "paper",
  customComponentTitle,
  fullScreen = false,
  sx = {},
  sxContent = {},
  refContent,
  hasTitle = true,
  titleContent = true,
  sxPaper = {},
  sxTitle = {},
  colorTitle = "black !important",
  variantTitle = "h5",
  description = "",
  variantDescription = "body1",
  sxDescription = {},
  dataTour = "",
  onKeyDown = () => {},
  showCloseButton = true,
  enableHistoryManagement = true,
  historyStateData = { modal: "default" },
  disableScrollLock = false,
  zIndex,
}) => {
  const theme = useTheme();
  const { totalTopOffsetPx } = useRebrandBanner();

  // Browser history control references
  const modalIdRef = useRef(generateModalId());
  const hasPushedHistoryRef = useRef(false);
  const modalOpenRef = useRef(false);
  const onCloseRef = useRef(onCloseModal);

  const modalId = modalIdRef.current;
  const resolvedZIndex = modalOpen ? registerModalLayer(modalId, zIndex) : null;

  useEffect(() => {
    if (!modalOpen) {
      unregisterModalLayer(modalId);
    }

    return () => {
      unregisterModalLayer(modalId);
    };
  }, [modalOpen, modalId]);

  const effectiveZIndex = zIndex ?? resolvedZIndex;

  useEffect(() => {
    modalOpenRef.current = modalOpen;
    onCloseRef.current = onCloseModal;
  }, [modalOpen, onCloseModal]);

  // Modal stack and history management
  useEffect(() => {
    if (!enableHistoryManagement) return;

    const modalId = modalIdRef.current;

    if (modalOpen && !hasPushedHistoryRef.current) {
      addGlobalPopstateListener();

      const currentAriaLabel = ariaLabel || "modal";
      const currentHistoryStateData = historyStateData || { modal: "default" };

      const stateData =
        currentHistoryStateData.modal === "default"
          ? { modal: currentAriaLabel, modalId }
          : { ...currentHistoryStateData, modalId };

      window.history.pushState(stateData, "");
      hasPushedHistoryRef.current = true;

      const modalInfo = {
        id: modalId,
        isOpen: true,
        hasPushedHistory: true,
        onClose: onCloseRef.current,
        ariaLabel: currentAriaLabel,
      };

      modalStack.push(modalInfo);
    }

    return () => {
      if (!modalOpen) {
        const modalInStack = modalStack.find((modal) => modal.id === modalId);
        if (modalInStack) {
          modalStack = modalStack.filter((modal) => modal.id !== modalId);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, enableHistoryManagement]);

  // Update modal state in stack
  useEffect(() => {
    const modalInStack = modalStack.find(
      (modal) => modal.id === modalIdRef.current
    );
    if (modalInStack) {
      modalInStack.isOpen = modalOpen;
      modalInStack.onClose = onCloseRef.current;
      modalInStack.hasPushedHistory = hasPushedHistoryRef.current;
    }
  }, [modalOpen, onCloseModal]);

  // Clean up when modal closes
  useEffect(() => {
    if (!modalOpen) {
      const modalInStack = modalStack.find(
        (modal) => modal.id === modalIdRef.current
      );
      if (modalInStack) {
        modalStack = modalStack.filter(
          (modal) => modal.id !== modalIdRef.current
        );
        cleanModalStack();
      }
    }
  }, [modalOpen]);

  // Handle direct modal close from X button (immediate close)
  const onDirectClose = () => {
    // Clean up history if exists
    if (enableHistoryManagement && hasPushedHistoryRef.current) {
      modalStack = modalStack.filter(
        (modal) => modal.id !== modalIdRef.current
      );
      hasPushedHistoryRef.current = false;
    }

    // Close modal immediately
    onCloseModal();
  };

  const actions = [];
  const content = [];
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === DialogActions) {
        actions.push(child);
      } else {
        content.push(child);
      }
    }
  });

  const titleDescriptionBlock =
    (title && titleContent) || description ? (
      <Box sx={{ mb: 2 }}>
        {title && titleContent && (
          <Typography
            variant={variantTitle}
            color="inherit"
            align="left"
            sx={{
              fontWeight: 600,
              ...sxTitle,
            }}
          >
            {title}
          </Typography>
        )}
        {description && (
          <Typography
            variant={variantDescription}
            align="left"
            sx={{
              ...sxDescription,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    ) : null;

  const modalBody = (
    <>
      <DialogContent
        sx={{
          px: {
            xs: 3,
            sm: 5,
          },
          pb: 2,
          ...sxContent,
        }}
        ref={refContent}
      >
        {titleDescriptionBlock}
        {content}
      </DialogContent>
      {actions}
    </>
  );

  return (
    <Dialog
      open={modalOpen}
      onClose={(e) => {
        e.stopPropagation();
        onDirectClose();
      }}
      disableScrollLock={disableScrollLock}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      scroll={scroll}
      aria-labelledby={ariaLabel}
      aria-describedby={ariaLabel}
      sx={{
        ...(effectiveZIndex !== undefined && effectiveZIndex !== null
          ? { zIndex: effectiveZIndex }
          : {}),
        "& .MuiDialog-paper": {
          backgroundImage: "none",
          backgroundColor: "background.paper",
          m: 0,
          maxHeight: fullScreen ? "100%" : "calc(100% - 8%)",
          borderRadius: fullScreen ? 0 : 4,
          width: fullScreen
            ? "100%"
            : {
                xs: "calc(100% - 24px)",
                sm: `calc(100% - ${totalTopOffsetPx}px)`,
              },
          ...sxPaper,
        },
        ...sx,
      }}
      PaperProps={{
        "data-tour": dataTour,
      }}
      onKeyDown={onKeyDown}
    >
      {hasTitle && (
        <DialogTitle
          sx={{
            m: 0,
            px: 2,
            pb: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 2,
            ...sxTitle,
          }}
          component="div"
        >
          {title && !titleContent && (
            <Typography
              variant={variantTitle}
              color="inherit"
              align="left"
              sx={{ fontWeight: 600, color: `${colorTitle} !important` }}
            >
              {title}
            </Typography>
          )}
          {customComponentTitle}
          {showCloseButton && (
            <IconButton
              className="close-modal-button"
              aria-label="close"
              onClick={(e) => {
                e.stopPropagation();
                onDirectClose();
              }}
              sx={{
                p: 0.5,
                fontSize: "1.4rem",
                color: "background.paperSecondaryContrastText",
                backgroundColor: "background.paperSecondary",
                "&:hover": {
                  color: "background.paperSecondaryContrastText",
                  backgroundColor: "background.paperSecondary",
                  filter: theme.palette.hover.main,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
      )}

      {modalBody}
    </Dialog>
  );
};

export default ModalBasicLayout;
